import React, { createContext, useContext, useEffect, useMemo, useState, useRef } from "react";
import { api, setSelectedCruiseIdProvider } from "@/lib/api";
import { toCalendarDate, todayLocalDate, voyageStageFor, daysBetween } from "@/lib/calendarDate";

const CruiseContext = createContext(null);

/**
 * Determine the voyage lifecycle stage from calendar-only dates.
 * PD-031 · BR-DATE-003 — delegates to the shared temporal library.
 */
export function computeStage(nowDate, embarkDate, disembarkDate) {
  const s = voyageStageFor(nowDate, embarkDate, disembarkDate);
  if (s === "pre-voyage") return "planning";
  if (s === "in-voyage") return "voyage";
  return "memories";
}

/**
 * CruiseProvider owns:
 *   - `config` — thin lifecycle payload from /api/voyage/config (stage-aware
 *     dates + destination names). Used for the countdown and top-line copy.
 *   - `voyage` — authoritative record from /api/cruises/current/workspace, including
 *     the pre-joined itinerary with port coordinates and approach waypoints
 *     (source of truth per ADR-004/006).
 *
 * ADR-006 fail-loud: if /api/cruises/current/workspace cannot be fetched we surface a
 * `voyageError` and stop rendering voyage-derived UI. No hard-coded fallbacks.
 */
export function CruiseProvider({ children }) {
  const [config, setConfig] = useState(null);
  const [voyage, setVoyage] = useState(null);
  const [voyageError, setVoyageError] = useState(null);
  const [firstRun, setFirstRun] = useState(false);
  const [today, setToday] = useState(null);
  const [todayError, setTodayError] = useState(null);
  const [todayLoaded, setTodayLoaded] = useState(false);
  const [overrideStage, setOverrideStage] = useState(null);
  const [now, setNow] = useState(new Date());
  const [loading, setLoading] = useState(true);
  // CKB-040 v4 · LAW-005 — the single session-level source of truth
  // for the selected cruise. Every cruise-aware workspace derives its
  // context from this value. Persisted in localStorage per user;
  // hydrated from `portfolio.next` when unset.
  const [portfolio, setPortfolio] = useState(null);
  // Hydrate `selectedCruiseId` synchronously from localStorage on the
  // very first render — critical for LAW-005 compliance during hard
  // refresh. If we deferred this to `useEffect`, `loadConfig` and
  // `loadToday` would race the ref-population and fire without the
  // `?voyage_id=` view-state override, causing the backend to fall
  // back to the deprecated `active_voyage_id` pointer and rendering a
  // different voyage than the Cruise Selector shows.
  const [selectedCruiseId, setSelectedCruiseIdState] = useState(() => {
    try {
      return window.localStorage.getItem("cc.selectedCruiseId") || null;
    } catch (_e) {
      return null;
    }
  });
  const [route, setRouteState] = useState(() => {
    try {
      // Phase 5 · hash format may carry a query string for sub-tab
      // deep-linking (e.g. `#knowledge?tab=studio`). Route parsing must
      // discard the query so the top-level route resolution still works.
      const hash = window.location.hash.replace(/^#/, "").split("?")[0];
      return hash || "dashboard";
    } catch {
      return "dashboard";
    }
  });

  const setRoute = (next) => {
    setRouteState(next);
    try {
      // Preserve any existing query string (sub-tab deep-link) when the
      // user clicks the same top-level route again, otherwise reset it.
      const currentHash = window.location.hash || "";
      const currentBase = currentHash.replace(/^#/, "").split("?")[0];
      const currentQuery = currentHash.includes("?")
        ? `?${currentHash.split("?")[1]}`
        : "";
      const suffix = currentBase === next ? currentQuery : "";
      window.history.replaceState(null, "", `#${next}${suffix}`);
    } catch (_e) { /* ignore */ }
  };

  // Sprint 4 · Destination Guide · 2026-07-14
  // Sync `route` React state whenever the browser hash changes (deep
  // links, direct URL edits, back/forward navigation). Without this
  // listener, hash-based routes like #destination-guide:<id> only
  // resolve on the initial page load — subsequent hash changes are
  // silently ignored.
  useEffect(() => {
    const onHash = () => {
      try {
        const hash = window.location.hash.replace(/^#/, "").split("?")[0];
        setRouteState(hash || "dashboard");
      } catch (_e) { /* ignore */ }
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // CKB-040 v4.1 · LAW-005 · Publish `selectedCruiseId` to the axios
  // interceptor so every outbound cruise-context-aware request carries
  // `?voyage_id=<selectedId>` automatically. Uses a ref so the getter
  // always returns the latest value (no stale closures). The ref is
  // initialised inline (not in a useEffect) so the interceptor sees the
  // hydrated value on the very first render — a hard-refresh Dashboard
  // hit must never race the localStorage-hydrated selection.
  const selectedCruiseIdRef = useRef(selectedCruiseId);
  // Set the provider ONCE, on the first render — so any API call fired
  // during mount (including from other Provider effects) sees the
  // correct getter.
  useMemo(() => {
    setSelectedCruiseIdProvider(() => selectedCruiseIdRef.current);
    return null;
  }, []);
  useEffect(() => {
    selectedCruiseIdRef.current = selectedCruiseId;
  }, [selectedCruiseId]);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const loadConfig = async (stage) => {
    const res = await api.get("/api/voyage/config", {
      params: stage ? { stage } : {},
    });
    setConfig(res.data);
  };

  const loadToday = async (stage) => {
    try {
      const res = await api.get("/api/cruises/current/today", {
        params: stage ? { stage } : {},
      });
      setToday(res.data);
      setTodayError(null);
    } catch (err) {
      // UX-loading-audit — never leave the UI stuck in "Loading…". Distinguish
      // "unavailable" (a legitimate empty state, e.g. no voyage yet) from
      // "errored" so consumers can render Empty vs Error, not perpetual Loading.
      setToday(null);
      const status = err?.response?.status;
      if (status && status !== 404) {
        setTodayError({ status, message: err?.message || "Unable to compose Today's Brief" });
      } else {
        setTodayError(null);
      }
    } finally {
      setTodayLoaded(true);
    }
  };

  const loadVoyage = React.useCallback(async (voyageIdOverride) => {
    try {
      const params = voyageIdOverride ? { voyage_id: voyageIdOverride } : {};
      const res = await api.get("/api/cruises/current/workspace", { params });
      setVoyage(res.data);
      setVoyageError(null);
      setFirstRun(false);
    } catch (e) {
      setVoyage(null);
      // PD-022 · No Seeded Operational Voyages.
      // A 404 from /api/cruises/current/workspace is the FIRST-RUN signal, not an
      // error. It means the operator has never created a voyage — the
      // honest baseline for a fresh install. Only surface `voyageError`
      // for genuine failures (network, 5xx, etc.).
      const status = e?.response?.status;
      if (status === 404) {
        setVoyageError(null);
        setFirstRun(true);
      } else {
        setVoyageError(e?.response?.data?.detail || e?.message || "Unable to load voyage");
        setFirstRun(false);
      }
    }
  }, []);

  // CKB-040 v4 · LAW-005 · Portfolio + Cruise Selector session context.
  const loadPortfolio = React.useCallback(async () => {
    try {
      const today = todayLocalDate();
      const res = await api.get("/api/cruises/portfolio", {
        params: today ? { today_client_date: today } : {},
      });
      setPortfolio(res.data);
      return res.data;
    } catch (_e) {
      setPortfolio(null);
      return null;
    }
  }, []);

  // LAW-005 · `selectedCruiseId` is client-owned view state — the sole
  // session-level value from which every cruise-aware workspace derives
  // its context. The backend owns travel state (booking_status, dates,
  // etc.); the frontend owns view state. Selecting a cruise therefore
  // makes NO backend write — it updates local state, persists to
  // localStorage, and refetches the workspace payload using the
  // `?voyage_id=…` view-state override on `/current/workspace`.
  const persistSelection = (id) => {
    try {
      if (id) window.localStorage.setItem("cc.selectedCruiseId", id);
      else window.localStorage.removeItem("cc.selectedCruiseId");
    } catch (_e) { /* ignore */ }
  };

  const selectCruise = React.useCallback(async (id) => {
    if (!id) return;
    // Update the ref synchronously so the interceptor sees the new id
    // on the very next request — before React commits the state
    // change. Otherwise `loadConfig`/`loadToday`/`loadVoyage` would
    // race the state update and fire with the previous voyage_id.
    selectedCruiseIdRef.current = id;
    setSelectedCruiseIdState(id);
    persistSelection(id);
    // RC7-M6 · Signal every voyage-scoped protected surface (Documents
    // reveal state, C3 memory) to drop sensitive/protected state before
    // the new voyage's data lands.
    try {
      if (typeof window !== "undefined" && window.dispatchEvent) {
        window.dispatchEvent(new Event("cc:voyage_switch"));
      }
    } catch { /* non-DOM env */ }
    await Promise.all([
      loadVoyage(id),
      loadConfig().catch(() => {}),
      loadToday(overrideStage),
    ]);
  }, [overrideStage, loadVoyage]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      // Boot: `selectedCruiseId` was already hydrated synchronously
      // from localStorage on first render, so `loadConfig` / `loadToday`
      // /`loadVoyage` all see the correct `?voyage_id=` from the very
      // first request. Portfolio still loads in parallel so we can
      // reconcile stale/invalid selections below.
      const [pf] = await Promise.all([
        loadPortfolio(),
        loadVoyage(selectedCruiseIdRef.current || undefined),
        loadConfig().catch(() => {}),
        loadToday(),
      ]);
      // Reconcile: if the saved selection is no longer authorised
      // (deleted, revoked share, or another account), choose the first
      // voyage from the caller's server-scoped portfolio. `next` remains
      // first when present; users with planning-only voyages must not be
      // misclassified as first-run after a revocation/account switch.
      try {
        const saved = selectedCruiseIdRef.current;
        const allRows = [
          pf?.next,
          ...(pf?.future?.items || []),
          ...(pf?.planning?.items || []),
          ...(pf?.archive?.completed?.items || []),
          ...(pf?.archive?.cancelled?.items || []),
        ].filter(Boolean);
        const validSaved = saved && allRows.some((v) => v?.id === saved);
        if (!validSaved) {
          const fallback = allRows[0]?.id || null;
          setSelectedCruiseIdState(fallback);
          selectedCruiseIdRef.current = fallback;
          persistSelection(fallback);
          if (fallback) {
            await loadVoyage(fallback);
          }
        }
      } catch (_e) { /* ignore reconciliation errors */ }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // PD-031 · BR-DATE-003 — expose calendar-date values, not JS Date objects.
  // Every consumer must go through @/lib/calendarDate; no `new Date(embark_date)`.
  const embarkDate = useMemo(
    () => toCalendarDate(config?.embark_date ?? config?.embark_at),
    [config],
  );
  const disembarkDate = useMemo(
    () => toCalendarDate(config?.disembark_date ?? config?.disembark_at),
    [config],
  );
  const nowDate = useMemo(() => todayLocalDate(), []);

  const autoStage = useMemo(() => {
    if (!embarkDate || !disembarkDate) return "planning";
    const s = voyageStageFor(nowDate, embarkDate, disembarkDate);
    // Legacy stage labels used by the workspaces.
    if (s === "pre-voyage") return "planning";
    if (s === "in-voyage") return "onboard";
    if (s === "post-voyage") return "memories";
    return "planning";
  }, [nowDate, embarkDate, disembarkDate]);

  const stage = overrideStage ?? autoStage;

  const setStage = async (nextStage) => {
    setOverrideStage(nextStage);
    await Promise.all([loadConfig(nextStage), loadToday(nextStage)]);
  };

  const clearOverride = () => setOverrideStage(null);

  // ----- Voyage-derived selectors --------------------------------------
  const derived = useMemo(() => {
    if (!voyage) {
      return {
        itinerary: [],
        portCalls: [],
        polyline: [],
        segments: [],
        activeSegmentIndex: -1,
        nextPortCall: null,
        currentPortCall: null,
        voyageMeta: null,
      };
    }
    const itinerary = voyage.itinerary || [];
    // Ordered port_calls that have a port (drop sea days for UI lists).
    const portCalls = itinerary.filter((pc) => pc.port);

    // Segments = each port_call with waypoints. First (embark) has none;
    // subsequent legs concatenate onto the polyline.
    const segments = itinerary
      .filter((pc) => Array.isArray(pc.approach_waypoints) && pc.approach_waypoints.length > 1)
      .map((pc, i, arr) => ({
        toPortCall: pc,
        waypoints: pc.approach_waypoints,
        index: i,
        length: arr.length,
      }));

    // Full polyline — concatenate waypoints in itinerary order, skipping
    // duplicated join points.
    const polyline = [];
    segments.forEach((s, i) => {
      const wp = s.waypoints;
      if (i === 0) polyline.push(...wp);
      else polyline.push(...wp.slice(1));
    });

    // Determine current position based on stage-adjusted embark date. Ship
    // sails a leg per day; sea days elongate the current leg.
    let currentPortCall = null;
    let nextPortCall = null;
    let activeSegmentIndex = -1;

    if (embarkDate && disembarkDate && stage === "voyage") {
      // PD-031 · position within the voyage is a calendar-day fraction —
      // never a JS Date millisecond math because DST or a viewer's tz
      // would otherwise shift the answer.
      const total = daysBetween(embarkDate, disembarkDate);
      const done = daysBetween(embarkDate, nowDate);
      const t = total ? Math.min(1, Math.max(0, done / total)) : 0;
      const idx = Math.min(itinerary.length - 1, Math.floor(t * itinerary.length));
      currentPortCall = itinerary[idx] ?? null;
      for (let i = idx; i < itinerary.length; i++) {
        if (itinerary[i]?.port) { nextPortCall = itinerary[i]; break; }
      }
      activeSegmentIndex = Math.max(
        0,
        segments.findIndex((s) => s.toPortCall === currentPortCall),
      );
    } else if (embarkDate && stage === "planning") {
      nextPortCall = portCalls.find((pc) => pc.kind !== "embark") || portCalls[0] || null;
      activeSegmentIndex = 0;
    } else {
      nextPortCall = null;
      activeSegmentIndex = segments.length - 1;
    }

    const voyageMeta = {
      id: voyage.id,
      name: voyage.name,
      ship_id: voyage.ship_id,
      ship_name: voyage.ship_name,
      ship_class: voyage.ship_class,
      cruise_line: voyage.cruise_line,
      region: voyage.region || "",
      total_days: voyage.total_days,
      nights: voyage.nights,
      home_port_id: voyage.home_port_id,
      guest_name: voyage.guest_name,
      stateroom: voyage.stateroom,
      // RC8 · canonical booked-cabin fields for the Ship Guide / Explore
      // module. `cabin_category_id` (when present) is the only signal used
      // for a deterministic reference match — never the number or the label.
      cabin_number: voyage.cabin_number,
      cabin_category: voyage.cabin_category,
      cabin_category_id: voyage.cabin_category_id,
      deck: voyage.deck,
      booking_number: voyage.booking_number,
      // PD-031 · calendar-date fields (canonical); `_at` retained for the
      // migration window only. New code must read `_date`.
      embark_date: voyage.embark_date ?? voyage.embark_at,
      disembark_date: voyage.disembark_date ?? voyage.disembark_at,
      embark_at: voyage.embark_date ?? voyage.embark_at,
      disembark_at: voyage.disembark_date ?? voyage.disembark_at,
      // MWO-003 · canonical operational financials block from the
      // Voyage. Every downstream consumer reads this — never a local
      // recomputation of cruise_fare / balance_due.
      financials: voyage.financials || null,
      cruise_fare: voyage.cruise_fare,
      deposit_amount: voyage.deposit_amount,
      deposit_due: voyage.deposit_due,
      final_payment_due: voyage.final_payment_due,
      fare_currency: voyage.fare_currency,
    };

    return {
      itinerary,
      portCalls,
      polyline,
      segments,
      activeSegmentIndex,
      nextPortCall,
      currentPortCall,
      voyageMeta,
    };
  }, [voyage, embarkDate, disembarkDate, nowDate, stage]);

  // RC2 · MXR-001 · Mobile shell — sidebar becomes a drawer below `md`. State
  // lives here so both `Sidebar` and `TopHeader` can control it.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => {
    // Auto-close the drawer whenever the traveler navigates.
    setMobileNavOpen(false);
  }, [route]);

  const value = {
    config,
    voyage,
    today,
    todayError,
    todayLoaded,
    reloadToday: () => loadToday(overrideStage),
    reloadVoyage: async () => {
      // Called after a POST /api/voyages so the shell flips out of first-run
      // into an operational state without a full page reload. Also refreshes
      // the portfolio so a newly-created cruise appears in the Selector
      // without a manual reload. Honours the current `selectedCruiseId`.
      await Promise.all([
        loadVoyage(selectedCruiseId || undefined),
        loadConfig().catch(() => {}),
        loadToday(overrideStage),
        loadPortfolio(),
      ]);
    },
    voyageError,
    firstRun,
    stage,
    autoStage,
    overrideStage,
    setStage,
    clearOverride,
    now,
    // CKB-040 v4 · LAW-005 — Cruise Selector session context.
    portfolio,
    reloadPortfolio: loadPortfolio,
    selectedCruiseId,
    selectCruise,
    returnToNext: async () => {
      const nextId = portfolio?.next?.id || null;
      if (nextId && nextId !== selectedCruiseId) {
        await selectCruise(nextId);
      }
    },
    // PD-031 · BR-DATE-003 · canonical calendar-date accessors.
    embarkDate,
    disembarkDate,
    nowDate,
    // Deprecated: kept for the migration release; new code MUST use
    // embarkDate / disembarkDate + @/lib/calendarDate helpers.
    embark: embarkDate,
    disembark: disembarkDate,
    daysUntilEmbark: daysBetween(nowDate, embarkDate),
    daysIntoVoyage: daysBetween(embarkDate, nowDate),
    loading,
    route,
    setRoute,
    mobileNavOpen,
    setMobileNavOpen,
    ...derived,
  };

  return <CruiseContext.Provider value={value}>{children}</CruiseContext.Provider>;
}

export function useCruise() {
  const ctx = useContext(CruiseContext);
  if (!ctx) throw new Error("useCruise must be used within CruiseProvider");
  return ctx;
}
